import React, { useState, useEffect, useContext } from 'react';
import { Form, Button, Modal } from 'react-bootstrap';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { FiMail, FiLock, FiArrowRight, FiCheckCircle } from 'react-icons/fi';
import api from '../services/api';
import { encryptPassword } from '../utils/auth';
import { AbilityContext } from '../config/ability';
import FeedbackModal from '../components/FeedbackModal';

const Login = () => {
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [feedback, setFeedback] = useState({ show: false, type: '', title: '', text: '' });
    const [showRecover, setShowRecover] = useState(false);
    const [recoverEmail, setRecoverEmail] = useState('');
    const [recoverMsg, setRecoverMsg] = useState('');
    const [recoverLoading, setRecoverLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const ability = useContext(AbilityContext);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('expired') === 'true') {
            setFeedback({ 
                show: true, 
                type: 'warning', 
                title: 'Sessão Expirada', 
                text: 'Sua sessão expirou. Por favor, faça login novamente por segurança.' 
            });
        }
    }, [location]);

    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            const response = await api.post('/login', { email, senha: encryptPassword(senha) });
            localStorage.setItem('token', response.data.access_token);
            if (response.data.refresh_token) {
                localStorage.setItem('refreshToken', response.data.refresh_token);
            }
            localStorage.setItem('user', JSON.stringify(response.data.user));
            
            // Update Abilities
            const rules = response.data.abilities || [];
            ability.update(rules);
            localStorage.setItem('abilities', JSON.stringify(rules));

            // Redirect based on user's profile home page preference
            const homePage = response.data.user.home_page || '/training-list';
            navigate(homePage);
            
        } catch (err) {
            console.error("Erro no login:", err);
            let msg = 'Falha no login. Verifique suas credenciais.';
            
            if (err.response) {
                // Erro retornado pelo servidor (4xx, 5xx)
                msg = `Erro (${err.response.status}): ${err.response.data?.msg || err.response.statusText || 'Falha no servidor'}`;
            } else if (err.request) {
                // Erro de rede ou servidor não respondeu (CORS, Timeout, Offline)
                msg = 'Erro de Conexão: O servidor não respondeu. Verifique sua internet.';
            } else {
                // Erro na configuração da requisição
                msg = `Erro Interno: ${err.message}`;
            }
            
            setFeedback({ 
                show: true, 
                type: 'danger', 
                title: 'Erro de Login', 
                text: msg 
            });
        }
    };

    const handleRecover = async (e) => {
        e.preventDefault();
        setRecoverLoading(true);
        setRecoverMsg('');
        try {
            const response = await api.post('/auth/recover', { email: recoverEmail });
            setRecoverMsg(response.data.msg);
        } catch (err) {
            const msg = err.response?.data?.msg || 'Erro ao processar solicitação.';
            setRecoverMsg(msg);
        } finally {
            setRecoverLoading(false);
        }
    };

    const resetRecoverModal = () => {
        setShowRecover(false);
        setRecoverEmail('');
        setRecoverMsg('');
    };

    return (
        <div className="tm-split-screen">
            {/* Lado Esquerdo: Imagem e Branding */}
            <div className="tm-brand-panel">
                <div className="tm-brand-content">
                    <div className="tm-brand-logo flex-column">
                        <img 
                            src="/Logo_titulo-nbg.png" 
                            alt="Confirma AI Logo" 
                            style={{ width: '420px', height: 'auto', marginBottom: '1rem' }} 
                        />                        
                    </div>                    
                </div>
            </div>

            {/* Lado Direito: Formulário */}            
            <div className="tm-auth-panel">
                <div className="tm-auth-container">
                    <img 
                        src="/logo_nbg.png" 
                        alt="Confirma AI Logo" 
                        className="tm-mobile-logo"
                        loading="lazy"
                        style={{ width: '230px', height: 'auto', marginBottom: '1rem' }}
                    />
                    <h2 className="tm-auth-title">Login</h2>
                    <p className="tm-auth-subtitle">Bem-vindo de volta! Por favor, insira seus dados.</p>
                    
                    <FeedbackModal 
                        show={feedback.show}
                        onHide={() => setFeedback({ ...feedback, show: false })}
                        type={feedback.type}
                        title={feedback.title}
                        message={feedback.text}
                    />
                    
                    <Form onSubmit={handleLogin}>
                        <div className="tm-input-group">
                            <Form.Control 
                                className="tm-input-modern" 
                                type="email" 
                                placeholder="Email Corporativo"
                                value={email} 
                                onChange={(e) => setEmail(e.target.value)} 
                                required 
                            />
                            <FiMail className="tm-input-icon" size={18} />
                        </div>
                        
                        <div className="tm-input-group">
                            <Form.Control 
                                className="tm-input-modern" 
                                type="password" 
                                placeholder="Senha"
                                value={senha} 
                                onChange={(e) => setSenha(e.target.value)} 
                                required 
                            />
                            <FiLock className="tm-input-icon" size={18} />
                        </div>

                        <div className="tm-flex-between">
                            <Form.Check 
                                type="checkbox" 
                                label={<span className="tm-text-small">Lembrar-me</span>} 
                                id="remember-me"
                            />
                            <button 
                                type="button"
                                className="btn btn-link p-0 tm-link" 
                                onClick={() => setShowRecover(true)}
                                style={{ fontSize: '0.875rem' }}
                            >
                                Esqueceu a senha?
                            </button>
                        </div>

                        <Button className="btn-success w-100 py-3 mb-4 d-flex align-items-center justify-content-center" type="submit" style={{ fontSize: '1.1rem' }}>
                            Entrar <FiArrowRight className="ms-2" />
                        </Button>
                    </Form>
                </div>
            </div>

            {/* Modal de Recuperação */}
            <Modal show={showRecover} onHide={resetRecoverModal} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="tm-auth-title" style={{ fontSize: '1.5rem' }}>Recuperar senha</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    {!recoverMsg ? (
                        <>
                            <p className="tm-text-small mb-4">Digite seu email para receber o link de redefinição.</p>
                            <Form onSubmit={handleRecover}>
                                <div className="tm-input-group mb-4">
                                    <Form.Control 
                                        className="tm-input-modern" 
                                        type="email" 
                                        placeholder="Seu email cadastrado"
                                        value={recoverEmail} 
                                        onChange={(e) => setRecoverEmail(e.target.value)} 
                                        required 
                                    />
                                    <FiMail className="tm-input-icon" size={18} />
                                </div>
                                <div className="d-flex gap-2">
                                    <Button variant="light" onClick={resetRecoverModal} className="flex-grow-1 border">Cancelar</Button>
                                    <Button variant="primary" type="submit" disabled={recoverLoading} className="flex-grow-1" style={{ backgroundColor: 'var(--tm-primary)', border: 'none' }}>
                                        {recoverLoading ? 'Enviando...' : 'Enviar'}
                                    </Button>
                                </div>
                            </Form>
                        </>
                    ) : (
                        <div className="text-center py-3">
                            <div className="mb-3 text-success">
                                <FiCheckCircle size={48} color="#69F0AE" />
                            </div>
                            <p className="mb-4 text-muted">{recoverMsg}</p>
                            <Button variant="primary" onClick={resetRecoverModal} className="w-100 fw-bold" style={{ backgroundColor: 'var(--tm-primary)', border: 'none' }}>
                                Entendi, vou verificar meu e-mail
                            </Button>
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default Login;
